import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useToast } from '../ui/use-toast';
import TeamEntriesCard from './TeamEntriesCard';
import TeamEntryDialog from './TeamEntryDialog';
import TeamReEntryDialog from './TeamReEntryDialog';
import { teamEntriesService, type TeamEntry } from '../../services/team-entries';
import type { TeamEntriesState } from './useTeamEntries';

interface TeamEntriesSectionProps {
  competitionSlug: string;
  /** Named in the withdrawal question: "out of the start list of …". */
  competitionName: string;
  /** The squads' names, known from the page before the list arrives. */
  teamNames: string[];
  /** The squads' entries, read by the page (useTeamEntries): the rounds' cards list them too. */
  squad: TeamEntriesState;
}

/** The query parameters the entry form lives at. */
const ENTRY_PARAM = 'team_entry';
const TEAM_PARAM = 'team';

/**
 * A team manager's squads at this competition: one card per squad, so entering
 * somebody from a card already says which squad they are for.
 *
 * Mounted by the competition's page only for somebody named on a squad here —
 * nearly nobody — and only while entries are open: afterwards the squad's
 * athletes are on the cards of the rounds they climb. The list is read once for
 * every squad, by the page (useTeamEntries).
 *
 * The entry form has its own URL, as the desk's does: `?team_entry=new&team=<id>`
 * enters somebody for that squad, `?team_entry=<id>` corrects one of the
 * squad's own rows. "Enter your athletes for Saturday" can then be a link the
 * organiser sends — the same one to every team manager, since `team` may be
 * left out and the form then opens on the first of the viewer's squads — and a
 * reload comes back to the form. A link the page cannot honour — entries
 * closed, a squad or a row that is not this person's — just leaves the form
 * shut.
 *
 * Where the stage lets the squad enter again the athletes who competed with
 * it at the circuit's other stages (`reEntry`), entering somebody opens on a
 * **search** over them first (TeamReEntryDialog), the same link included; its
 * "Nuovo atleta" falls through to the empty form, and the empty form can go
 * back to the search. Which of the two is showing is the page's own state, not
 * the URL's: the link stays the one the organiser sent.
 */
export default function TeamEntriesSection({
  competitionSlug,
  competitionName,
  teamNames,
  squad,
}: TeamEntriesSectionProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, loading, loadFailed, reload: load } = squad;
  const [withdrawing, setWithdrawing] = useState<TeamEntry | null>(null);
  const [removing, setRemoving] = useState(false);
  // "Nuovo atleta" was asked for instead of the squad's search.
  const [blankForm, setBlankForm] = useState(false);

  /** Opens the form at its own URL, or closes it. */
  const openForm = (entry: string | null, teamId?: string) => {
    setBlankForm(false);
    const next = new URLSearchParams(searchParams);
    if (entry) next.set(ENTRY_PARAM, entry);
    else next.delete(ENTRY_PARAM);
    if (teamId) next.set(TEAM_PARAM, teamId);
    else next.delete(TEAM_PARAM);
    setSearchParams(next, { replace: !entry });
  };

  // What the URL asks for, if the page can give it: a squad of this person's
  // to enter somebody for (the first of them, in the cards' order, when the
  // link names none), or one of the squad's own rows to correct. A link that
  // names somebody else's squad is a wrong link, not a general one, and stays
  // shut.
  const entryParam = searchParams.get(ENTRY_PARAM);
  const teamParam = searchParams.get(TEAM_PARAM);
  const writable = !!page?.open;
  const creatingFor =
    writable && entryParam === 'new'
      ? teamParam
        ? page.teams.find((team) => team.id === teamParam) ?? null
        : page.teams[0] ?? null
      : null;
  const editing =
    writable && entryParam && entryParam !== 'new'
      ? page.entries.find((entry) => entry.id === entryParam && entry.editable) ?? null
      : null;
  // Entering somebody opens on the squad's search where the stage offers it.
  const searching = !!creatingFor && !!page?.reEntry && !blankForm;

  const saved = (entry: TeamEntry, created: boolean) => {
    openForm(null);
    toast({
      title: created ? t('teamEntries.entered', { name: entry.full_name }) : t('teamEntries.saved'),
    });
    load();
  };

  const withdraw = async () => {
    if (!withdrawing || removing) return;
    setRemoving(true);
    try {
      await teamEntriesService.withdraw(competitionSlug, withdrawing.id);
      toast({ title: t('teamEntries.withdrawn', { name: withdrawing.full_name }) });
      setWithdrawing(null);
      load();
    } catch (error) {
      toast({
        title: t('teamEntries.withdrawError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setRemoving(false);
    }
  };

  // Before the list arrives the squads are known by name only, which is enough
  // to draw their cards.
  const teams: { id: string | null; name: string }[] = page?.teams ?? teamNames.map((name) => ({ id: null, name }));

  return (
    <div className="space-y-3" data-testid="team-entries">
      {teams.map((team) => (
        <TeamEntriesCard
          // By name, which is unique in a season and known before the list
          // arrives: keyed by id, a card opened while loading would be
          // remounted — and folded again — the moment the ids came in.
          key={team.name}
          teamName={team.name}
          entries={page?.entries.filter((entry) => entry.team_id === team.id) ?? []}
          loading={loading}
          loadFailed={loadFailed}
          open={writable}
          blockedReason={page?.blockedReason ?? null}
          onAdd={team.id ? () => openForm('new', team.id!) : undefined}
          onEdit={(entry) => openForm(entry.id)}
          onWithdraw={setWithdrawing}
        />
      ))}

      <TeamReEntryDialog
        open={searching}
        competitionSlug={competitionSlug}
        team={creatingFor}
        onClose={() => openForm(null)}
        onSaved={saved}
        onNewAthlete={() => setBlankForm(true)}
      />

      <TeamEntryDialog
        open={(!!creatingFor && !searching) || !!editing}
        competitionSlug={competitionSlug}
        team={creatingFor ?? (editing ? { id: editing.team_id ?? '', name: editing.team_name ?? '' } : null)}
        entry={editing}
        genderCollected={!!page?.genderCollected}
        onClose={() => openForm(null)}
        onSaved={saved}
        onSearch={creatingFor && page?.reEntry ? () => setBlankForm(false) : undefined}
      />

      <AlertDialog open={!!withdrawing} onOpenChange={(open) => !open && !removing && setWithdrawing(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('teamEntries.withdrawDialog.title', { name: withdrawing?.full_name ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('teamEntries.withdrawDialog.description', {
                name: withdrawing?.full_name ?? '',
                stage: competitionName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-sm text-muted-foreground">{t('teamEntries.withdrawDialog.refundWarning')}</p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>{t('teamEntries.dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                withdraw();
              }}
              disabled={removing}
              data-testid="team-entry-withdraw-confirm"
            >
              {removing && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('teamEntries.withdrawDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
